<script lang="ts">
  import { confirmAction, scheduleUndo } from '$lib/state/actionDialog.svelte';
  // The unmatched-sender review queue: grouped rows + every row action (add
  // person, link to existing person, attach to site, mark internal, ignore,
  // admin-only add company/track domain, bulk import) plus the batch-select
  // bar. Extracted out of EmailsPage so the exact same markup/actions can be
  // reused for Admin > Emails > All inboxes' per-user "View review queue" —
  // only the API calls it makes differ (reviewApi/backfillApi), not the
  // rendering. See internal/api/handlers/email_review.go for the backend
  // this drives (both the /me and, for the admin caller, /admin/users/:id
  // on-behalf-of variants share the exact same response shapes).
  //
  // The backfill engine now runs a per-account FIFO queue (see
  // emailsync.Engine's backfillQueues), so any number of rows may be
  // running/queued at once — each row's own "importing…"/"queued (n)"
  // badge is derived straight from the polled queue (rowBackfillState)
  // instead of a single client-tracked "active" address.
  import { createQuery, createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { UserPlus, Users, Link2, Building2, Globe, EyeOff, UserCheck, LoaderCircle, MapPin } from 'lucide-svelte';
  import { peopleApi, companiesQuickCreateApi } from '$lib/api/crm';
  import { adminTrackedDomainsApi } from '$lib/api/admin';
  import { getErrorMessage } from '$lib/api/client';
  import { session } from '$lib/state/session.svelte';
  import { toast } from '$lib/state/toast.svelte';
  import Modal from '$lib/components/ui/Modal.svelte';
  import FilterChipRow from '$lib/components/ui/FilterChipRow.svelte';
  import SelectionBar from '$lib/components/ui/SelectionBar.svelte';
  import PersonCombobox from '$lib/components/ui/PersonCombobox.svelte';
  import CompanyCombobox from '$lib/components/ui/CompanyCombobox.svelte';
  import type { BackfillState, BackfillStatus, CompanyLookup, EmailReviewGroup } from '$lib/types';

  type ReviewAPI = {
    list: () => Promise<{ groups: EmailReviewGroup[] }>;
    ignore: (counterparty: string) => Promise<{ detail: string }>;
    ignoreBatch: (counterparties: string[]) => Promise<{ detail: string; count: number }>;
    markInternal: (counterparty: string) => Promise<{ detail: string }>;
  };
  type BackfillAPI = {
    start: (counterparty: string) => Promise<{ detail: string; status: BackfillState; position: number }>;
    status: () => Promise<BackfillStatus>;
  };

  let {
    reviewApi,
    backfillApi,
    queryKey,
    enabled = true
  }: { reviewApi: ReviewAPI; backfillApi: BackfillAPI; queryKey: unknown[]; enabled?: boolean } = $props();

  const queryClient = useQueryClient();

  // Free/public email providers a tracked domain must never be — mirrors
  // handlers.blockedEmailDomains (internal/api/handlers/admin_tracked_domains.go)
  // and PeoplePage.svelte's identical copy; small and rarely-changing enough
  // that duplicating beats a shared util (the server is still the source of
  // truth and would 400 on a false negative here).
  const FREE_EMAIL_DOMAINS = new Set([
    'gmail.com',
    'googlemail.com',
    'hotmail.com',
    'outlook.com',
    'live.com',
    'icloud.com',
    'aol.com',
    'proton.me',
    'protonmail.com',
    'rediffmail.com',
    'zoho.com'
  ]);
  function isFreeEmailDomain(domain: string): boolean {
    return FREE_EMAIL_DOMAINS.has(domain) || domain === 'yahoo.com' || domain.startsWith('yahoo.');
  }

  function isAccessRevokedError(err: unknown): boolean {
    return getErrorMessage(err).toLowerCase().includes('not enabled for your account');
  }

  function invalidateReview() {
    queryClient.invalidateQueries({ queryKey });
  }

  function timeAgo(iso: string): string {
    const ms = Date.now() - new Date(iso).getTime();
    const min = Math.floor(ms / 60000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const days = Math.floor(hr / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(iso).toLocaleDateString();
  }

  const reviewQuery = createQuery(() => ({
    queryKey,
    queryFn: () => reviewApi.list(),
    enabled
  }));

  // Default to the API's own order (newest conversation first — see
  // listEmailReviewFor's doc comment); "Most emails" is an explicit toggle,
  // not the default anymore.
  let sortMode = $state<'newest' | 'count'>('newest');
  const groups = $derived(
    sortMode === 'count'
      ? [...(reviewQuery.data?.groups ?? [])].sort((a, b) => b.total - a.total)
      : (reviewQuery.data?.groups ?? [])
  );

  // ===================== backfill (per-account FIFO queue) =====================
  const backfillStatusQuery = createQuery(() => ({
    queryKey: [...queryKey, 'backfill-status'],
    queryFn: () => backfillApi.status(),
    enabled,
    refetchInterval: (q) => {
      const d = q.state.data;
      return d && (d.running || d.queued.length > 0) ? 3000 : false;
    }
  }));

  // Diffs the active-counterparty set on every poll; when something that was
  // running/queued drops out, treat it as finished (the item itself isn't
  // retained once it's popped off the queue — see BackfillStatus's doc
  // comment on the backend) and refetch the review list.
  let prevActiveKeys = new Set<string>();
  $effect(() => {
    const data = backfillStatusQuery.data;
    if (!data) return;
    const current = new Set<string>();
    if (data.running) current.add(data.running.counterparty.toLowerCase());
    for (const item of data.queued) current.add(item.counterparty.toLowerCase());
    const finished = [...prevActiveKeys].some((k) => !current.has(k));
    prevActiveKeys = current;
    if (finished) invalidateReview();
  });

  function counterpartyKeysForRow(row: EmailReviewGroup): string[] {
    return [row.addr.toLowerCase(), `@${row.domain.toLowerCase()}`];
  }

  // A row is "running" if its own address or its whole domain is the
  // queue's currently-running item; "queued" gives its 1-based position
  // among the still-pending items.
  function rowBackfillState(
    row: EmailReviewGroup
  ): { kind: 'running' } | { kind: 'queued'; position: number } | null {
    const data = backfillStatusQuery.data;
    if (!data) return null;
    const keys = counterpartyKeysForRow(row);
    if (data.running && keys.includes(data.running.counterparty.toLowerCase())) return { kind: 'running' };
    const idx = data.queued.findIndex((item) => keys.includes(item.counterparty.toLowerCase()));
    return idx >= 0 ? { kind: 'queued', position: idx + 1 } : null;
  }

  const startBackfillMutation = createMutation(() => ({
    mutationFn: (counterparty: string) => backfillApi.start(counterparty),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [...queryKey, 'backfill-status'] }),
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));

  function triggerBackfill(counterparty: string, label: string) {
    toast.info(`Importing history for ${label}…`);
    startBackfillMutation.mutate(counterparty);
  }

  // Preselects the vertical only when it's unambiguous: the user's single
  // vertical, or an admin's currently-selected header vertical. Otherwise
  // leaves it unset so the modal forces an explicit choice — no silent
  // verticals[0] fallback for multi-vertical users (see build brief item 1/2).
  function initialVerticalId(): number | undefined {
    const verticals = session.me?.verticals ?? [];
    if (verticals.length <= 1) return verticals[0]?.id;
    return session.selectedVertical !== 'all' ? session.selectedVertical : undefined;
  }

  function verticalName(id: number | undefined): string {
    if (id === undefined) return '—';
    return session.me?.verticals.find((v) => v.id === id)?.name ?? '—';
  }

  // ===================== Attach to site =====================
  // "Attach to site" is Hotels-only (the hotel/brand/owner hierarchy — see
  // applyHotelHierarchy on the backend — doesn't exist outside that
  // vertical), so the button only renders when the caller can see a Hotels
  // vertical at all.
  const hotelsVerticals = $derived((session.me?.verticals ?? []).filter((v) => v.slug === 'hotels'));

  function initialHotelsVerticalId(): number | undefined {
    if (hotelsVerticals.length <= 1) return hotelsVerticals[0]?.id;
    return hotelsVerticals.some((v) => v.id === session.selectedVertical) ? (session.selectedVertical as number) : undefined;
  }

  let attachSiteRow = $state<EmailReviewGroup | null>(null);
  let attachSiteForm = $state({
    first_name: '',
    last_name: '',
    vertical_id: undefined as number | undefined,
    hotel_company_id: undefined as number | undefined,
    brand_company_id: undefined as number | undefined,
    owner_company_id: undefined as number | undefined
  });
  let attachSiteError = $state('');

  function openAttachSite(row: EmailReviewGroup) {
    const parts = row.name.trim().split(/\s+/).filter(Boolean);
    attachSiteForm = {
      first_name: parts[0] ?? '',
      last_name: parts.slice(1).join(' '),
      vertical_id: initialHotelsVerticalId(),
      hotel_company_id: undefined,
      brand_company_id: undefined,
      owner_company_id: undefined
    };
    attachSiteError = '';
    attachSiteRow = row;
  }

  // Same additive-only auto-fill as PeoplePage's onHotelSelect: never
  // overwrites a field the user already picked.
  function onAttachHotelSelect(c: CompanyLookup) {
    if (attachSiteForm.brand_company_id === undefined && c.brand_company_id) {
      attachSiteForm.brand_company_id = c.brand_company_id;
    }
    if (attachSiteForm.owner_company_id === undefined && c.owner_company_id) {
      attachSiteForm.owner_company_id = c.owner_company_id;
    }
  }

  const attachSiteMutation = createMutation(() => ({
    mutationFn: () =>
      peopleApi.create({
        vertical_id: attachSiteForm.vertical_id,
        first_name: attachSiteForm.first_name.trim(),
        last_name: attachSiteForm.last_name.trim(),
        email: attachSiteRow?.addr,
        hotel_company_id: attachSiteForm.hotel_company_id ?? null,
        brand_company_id: attachSiteForm.brand_company_id ?? null,
        owner_company_id: attachSiteForm.owner_company_id ?? null
      }),
    onSuccess: () => {
      const row = attachSiteRow!;
      attachSiteRow = null;
      // The site's own views (People list on SiteDashboard, the sites
      // grid/rollup) read through queries this write just made stale.
      queryClient.invalidateQueries({ queryKey: ['people'] });
      queryClient.invalidateQueries({ queryKey: ['site-people'] });
      queryClient.invalidateQueries({ queryKey: ['site-activities'] });
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      triggerBackfill(row.addr, row.addr);
    },
    onError: (err: unknown) => (attachSiteError = getErrorMessage(err))
  }));

  function submitAttachSite(e: SubmitEvent) {
    e.preventDefault();
    if (!attachSiteForm.first_name.trim() && !attachSiteForm.last_name.trim()) {
      attachSiteError = 'First or last name is required';
      return;
    }
    if (!attachSiteForm.vertical_id) {
      attachSiteError = 'Vertical is required';
      return;
    }
    if (!attachSiteForm.hotel_company_id) {
      attachSiteError = 'Pick or add the site (hotel/property)';
      return;
    }
    attachSiteError = '';
    attachSiteMutation.mutate();
  }

  // Any admin-gated "also allow this domain" checkbox (Add person, Link
  // person) routes through this one mutation.
  const quickTrackDomainMutation = createMutation(() => ({
    mutationFn: (body: { domain: string; vertical_id?: number; company_id: number }) => adminTrackedDomainsApi.create(body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-tracked-domains'] }),
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));

  // ===================== Add person =====================
  let addPersonRow = $state<EmailReviewGroup | null>(null);
  let addPersonForm = $state({
    first_name: '',
    last_name: '',
    vertical_id: undefined as number | undefined,
    company_id: undefined as number | undefined
  });
  let addPersonError = $state('');
  let addPersonAlsoTrackDomain = $state(true);

  function openAddPerson(row: EmailReviewGroup) {
    const parts = row.name.trim().split(/\s+/).filter(Boolean);
    addPersonForm = {
      first_name: parts[0] ?? '',
      last_name: parts.slice(1).join(' '),
      vertical_id: initialVerticalId(),
      company_id: undefined
    };
    addPersonError = '';
    addPersonAlsoTrackDomain = true;
    addPersonRow = row;
  }

  // Only offered when an admin has picked a company for a corporate
  // (non-free-mail) sender — same gating as the existing "Track domain
  // only" row action.
  const showAddPersonTrackDomain = $derived(
    session.isAdmin && !!addPersonRow && !isFreeEmailDomain(addPersonRow.domain) && addPersonForm.company_id !== undefined
  );

  const addPersonMutation = createMutation(() => ({
    mutationFn: () =>
      peopleApi.create({
        vertical_id: addPersonForm.vertical_id,
        first_name: addPersonForm.first_name.trim(),
        last_name: addPersonForm.last_name.trim(),
        email: addPersonRow?.addr,
        company_id: addPersonForm.company_id ?? undefined
      }),
    onSuccess: () => {
      const row = addPersonRow!;
      const trackDomain = showAddPersonTrackDomain && addPersonAlsoTrackDomain;
      const companyId = addPersonForm.company_id;
      const verticalId = addPersonForm.vertical_id;
      addPersonRow = null;
      triggerBackfill(row.addr, row.addr);
      if (trackDomain && companyId !== undefined) {
        quickTrackDomainMutation.mutate({ domain: row.domain, vertical_id: verticalId, company_id: companyId });
      }
    },
    onError: (err: unknown) => (addPersonError = getErrorMessage(err))
  }));

  function submitAddPerson(e: SubmitEvent) {
    e.preventDefault();
    if (!addPersonForm.first_name.trim() && !addPersonForm.last_name.trim()) {
      addPersonError = 'First or last name is required';
      return;
    }
    if (!addPersonForm.vertical_id) {
      addPersonError = 'Vertical is required';
      return;
    }
    addPersonError = '';
    addPersonMutation.mutate();
  }

  // ===================== Link to existing person =====================
  let linkPersonRow = $state<EmailReviewGroup | null>(null);
  let linkPersonId = $state<number | undefined>(undefined);
  let linkPersonError = $state('');
  let linkAlsoTrackDomain = $state(true);

  function openLinkPerson(row: EmailReviewGroup) {
    linkPersonId = undefined;
    linkPersonError = '';
    linkAlsoTrackDomain = true;
    linkPersonRow = row;
  }

  const linkCandidateQuery = createQuery(() => ({
    queryKey: ['person-detail', linkPersonId],
    queryFn: () => peopleApi.get(linkPersonId as number),
    enabled: linkPersonId !== undefined
  }));

  // Only offered when the picked person already has a company on file — the
  // domain is "known" from them, never picked separately in this modal.
  const showLinkTrackDomain = $derived(
    session.isAdmin && !!linkPersonRow && !isFreeEmailDomain(linkPersonRow.domain) && !!linkCandidateQuery.data?.company_id
  );

  const linkPersonMutation = createMutation(() => ({
    mutationFn: () => peopleApi.update(linkPersonId as number, { email: linkPersonRow?.addr }),
    onSuccess: () => {
      const row = linkPersonRow!;
      const trackDomain = showLinkTrackDomain && linkAlsoTrackDomain;
      const companyId = linkCandidateQuery.data?.company_id;
      const verticalId = linkCandidateQuery.data?.vertical_id;
      linkPersonRow = null;
      triggerBackfill(row.addr, row.addr);
      if (trackDomain && companyId) {
        quickTrackDomainMutation.mutate({ domain: row.domain, vertical_id: verticalId, company_id: companyId });
      }
    },
    onError: (err: unknown) => (linkPersonError = getErrorMessage(err))
  }));

  async function confirmLinkPerson() {
    if (linkPersonId === undefined) {
      linkPersonError = 'Pick a person first';
      return;
    }
    const current = linkCandidateQuery.data?.email;
    if (current && current !== linkPersonRow?.addr) {
      const who = `${linkCandidateQuery.data?.first_name} ${linkCandidateQuery.data?.last_name}`.trim();
      if (!await confirmAction(`${who || 'This person'} already has the email "${current}" — overwrite it with "${linkPersonRow?.addr}"?`)) {
        return;
      }
    }
    linkPersonError = '';
    linkPersonMutation.mutate();
  }

  // ===================== Add company & track domain (admin) =====================
  let addCompanyRow = $state<EmailReviewGroup | null>(null);
  let addCompanyForm = $state({ name: '', city: '', kind: 'company', vertical_id: undefined as number | undefined });
  let addCompanyError = $state('');

  // Upper-cases each word's first letter — mirrors the comboboxes' own
  // titleCaseWords helper — before guessing a company name from a domain
  // label like "acme-hotels" -> "Acme Hotels".
  function titleCaseWords(s: string): string {
    return s.trim().split(/\s+/).filter(Boolean).map((w) => w[0].toUpperCase() + w.slice(1)).join(' ');
  }
  function nameGuessFromDomain(domain: string): string {
    return titleCaseWords((domain.split('.')[0] ?? domain).replace(/[-_]+/g, ' '));
  }

  function openAddCompany(row: EmailReviewGroup) {
    addCompanyForm = { name: nameGuessFromDomain(row.domain), city: '', kind: 'company', vertical_id: initialVerticalId() };
    addCompanyError = '';
    addCompanyRow = row;
  }

  const addCompanyMutation = createMutation(() => ({
    mutationFn: async () => {
      const company = await companiesQuickCreateApi.create(
        { name: addCompanyForm.name.trim(), kind: addCompanyForm.kind, city: addCompanyForm.city.trim() || undefined },
        addCompanyForm.vertical_id
      );
      await adminTrackedDomainsApi.create({
        domain: addCompanyRow?.domain,
        vertical_id: addCompanyForm.vertical_id,
        company_id: company.id
      });
    },
    onSuccess: () => {
      const row = addCompanyRow!;
      addCompanyRow = null;
      queryClient.invalidateQueries({ queryKey: ['admin-tracked-domains'] });
      triggerBackfill(`@${row.domain}`, `@${row.domain}`);
    },
    onError: (err: unknown) => (addCompanyError = getErrorMessage(err))
  }));

  function submitAddCompany(e: SubmitEvent) {
    e.preventDefault();
    if (!addCompanyForm.name.trim() || !addCompanyForm.vertical_id) {
      addCompanyError = 'Name and vertical are required';
      return;
    }
    addCompanyError = '';
    addCompanyMutation.mutate();
  }

  // ===================== Track domain only (admin, existing company) =====================
  let trackDomainRow = $state<EmailReviewGroup | null>(null);
  let trackDomainCompanyId = $state<number | undefined>(undefined);
  // Set by CompanyCombobox's bind:selectedVerticalId/selectedName as soon as
  // a company is picked (search or quick-create) — no separate unscoped
  // companies fetch needed anymore, the combobox's own live search already
  // returns vertical_id/name per result (see companyLookupJSON).
  let trackDomainVerticalId = $state<number | undefined>(undefined);
  let trackDomainCompanyName = $state<string | undefined>(undefined);
  let trackDomainError = $state('');

  function openTrackDomain(row: EmailReviewGroup) {
    trackDomainCompanyId = undefined;
    trackDomainVerticalId = undefined;
    trackDomainCompanyName = undefined;
    trackDomainError = '';
    trackDomainRow = row;
  }

  const trackDomainMutation = createMutation(() => ({
    mutationFn: () =>
      adminTrackedDomainsApi.create({
        domain: trackDomainRow?.domain,
        vertical_id: trackDomainVerticalId,
        company_id: trackDomainCompanyId
      }),
    onSuccess: () => {
      const row = trackDomainRow!;
      trackDomainRow = null;
      queryClient.invalidateQueries({ queryKey: ['admin-tracked-domains'] });
      triggerBackfill(`@${row.domain}`, `@${row.domain}`);
    },
    onError: (err: unknown) => (trackDomainError = getErrorMessage(err))
  }));

  function submitTrackDomain(e: SubmitEvent) {
    e.preventDefault();
    if (trackDomainCompanyId === undefined) {
      trackDomainError = 'Pick a company first';
      return;
    }
    trackDomainError = '';
    trackDomainMutation.mutate();
  }

  // ===================== Ignore =====================
  let ignoreError = $state('');
  const ignoreMutation = createMutation(() => ({
    mutationFn: (addr: string) => reviewApi.ignore(addr),
    onSuccess: () => invalidateReview(),
    onError: (err: unknown) => (ignoreError = getErrorMessage(err))
  }));

  function ignoreRow(row: EmailReviewGroup) {
    scheduleUndo(`Ignore sender “${row.addr}”`, () => {
      ignoreError = '';
      ignoreMutation.mutate(row.addr);
    });
  }

  // ===================== Mark internal (per row) =====================
  const markInternalMutation = createMutation(() => ({
    mutationFn: (addr: string) => reviewApi.markInternal(addr),
    onSuccess: (_result, addr) => {
      invalidateReview();
      toast.success(`Marked "${addr}" as internal — removed from everyone's review queue.`);
    },
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));

  async function markInternalRow(row: EmailReviewGroup) {
    if (
      await confirmAction(
        `Mark "${row.addr}" as internal (one of our own people)? This is org-wide — it removes this sender from every teammate's review queue, not just yours.`
      )
    ) {
      markInternalMutation.mutate(row.addr);
    }
  }

  // ===================== Batch select + bulk actions =====================
  const maxIgnoreBatch = 100; // mirrors the server cap in email_review.go
  let selected = $state<Set<string>>(new Set());
  const allSelected = $derived(groups.length > 0 && groups.every((g) => selected.has(g.addr)));

  function toggleRow(addr: string) {
    const next = new Set(selected);
    if (next.has(addr)) next.delete(addr);
    else next.add(addr);
    selected = next;
  }
  function toggleSelectAll() {
    selected = allSelected ? new Set() : new Set(groups.map((g) => g.addr));
  }

  async function chunkedIgnore(addrs: string[]): Promise<number> {
    let count = 0;
    for (let i = 0; i < addrs.length; i += maxIgnoreBatch) {
      const chunk = addrs.slice(i, i + maxIgnoreBatch);
      const res = await reviewApi.ignoreBatch(chunk);
      count += res.count ?? chunk.length;
    }
    return count;
  }

  const bulkIgnoreMutation = createMutation(() => ({
    mutationFn: (addrs: string[]) => chunkedIgnore(addrs),
    onSuccess: (count) => {
      invalidateReview();
      selected = new Set();
      toast.success(`Ignored ${count} sender${count === 1 ? '' : 's'}.`);
    },
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));

  function bulkIgnore() {
    const addrs = [...selected];
    if (addrs.length === 0) return;
    scheduleUndo(`Ignore ${addrs.length} selected senders`, () => {
      bulkIgnoreMutation.mutate(addrs);
    });
  }

  // No batch endpoint for internal — loop the single-counterparty call
  // sequentially; it's idempotent so a partial failure is safe to retry.
  async function chunkedMarkInternal(addrs: string[]): Promise<number> {
    for (const addr of addrs) {
      await reviewApi.markInternal(addr);
    }
    return addrs.length;
  }

  const bulkInternalMutation = createMutation(() => ({
    mutationFn: (addrs: string[]) => chunkedMarkInternal(addrs),
    onSuccess: (count) => {
      invalidateReview();
      selected = new Set();
      toast.success(`Marked ${count} sender${count === 1 ? '' : 's'} as internal.`);
    },
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));

  async function bulkMarkInternal() {
    const addrs = [...selected];
    if (addrs.length === 0) return;
    if (
      await confirmAction(
        `Mark ${addrs.length} selected sender${addrs.length === 1 ? '' : 's'} as internal? This is org-wide — it removes them from every teammate's review queue.`
      )
    ) {
      bulkInternalMutation.mutate(addrs);
    }
  }

  // ===================== Bulk import (selected senders, grouped by domain) =====================
  type BulkStatus = { state: 'running' | 'done' | 'error'; error?: string };
  type BulkDomainGroup = { domain: string; rows: EmailReviewGroup[]; corporate: boolean };

  let showBulkImport = $state(false);
  let bulkRows = $state<EmailReviewGroup[]>([]);
  // Per-domain company pick (corporate bucket) and per-address quick-add
  // form (free-mail / non-admin bucket) — both pre-seeded in openBulkImport
  // so every row/domain has a defined entry to bind: against before render.
  let bulkDomainForm = $state<Record<string, { company_id?: number; vertical_id?: number; company_name?: string }>>({});
  let bulkPersonForm = $state<
    Record<string, { first_name: string; last_name: string; vertical_id?: number; company_id?: number }>
  >({});
  let bulkStatus = $state<Record<string, BulkStatus>>({});
  let bulkSubmitting = $state(false);

  const bulkDomainGroups = $derived.by<BulkDomainGroup[]>(() => {
    const map = new Map<string, EmailReviewGroup[]>();
    for (const row of bulkRows) {
      const list = map.get(row.domain) ?? [];
      list.push(row);
      map.set(row.domain, list);
    }
    return [...map.entries()].map(([domain, rows]) => ({
      domain,
      rows,
      corporate: session.isAdmin && !isFreeEmailDomain(domain)
    }));
  });

  function openBulkImport() {
    bulkRows = groups.filter((g) => selected.has(g.addr));
    const domainForm: typeof bulkDomainForm = {};
    const personForm: typeof bulkPersonForm = {};
    for (const row of bulkRows) {
      if (session.isAdmin && !isFreeEmailDomain(row.domain)) {
        domainForm[row.domain] ??= {};
      } else {
        const parts = row.name.trim().split(/\s+/).filter(Boolean);
        personForm[row.addr] = {
          first_name: parts[0] ?? '',
          last_name: parts.slice(1).join(' '),
          vertical_id: initialVerticalId()
        };
      }
    }
    bulkDomainForm = domainForm;
    bulkPersonForm = personForm;
    bulkStatus = {};
    bulkSubmitting = false;
    showBulkImport = true;
  }

  // Sequential, not parallel — per-item progress is shown as it happens, and
  // the server queues the backfills anyway so there's nothing to gain from
  // firing them all at once.
  async function submitBulkImport() {
    bulkSubmitting = true;
    for (const g of bulkDomainGroups) {
      if (g.corporate) {
        const key = `domain:${g.domain}`;
        const form = bulkDomainForm[g.domain];
        if (!form?.company_id) {
          bulkStatus[key] = { state: 'error', error: 'Pick a company first' };
          continue;
        }
        bulkStatus[key] = { state: 'running' };
        try {
          await adminTrackedDomainsApi.create({ domain: g.domain, vertical_id: form.vertical_id, company_id: form.company_id });
          queryClient.invalidateQueries({ queryKey: ['admin-tracked-domains'] });
          await backfillApi.start(`@${g.domain}`);
          bulkStatus[key] = { state: 'done' };
        } catch (err) {
          bulkStatus[key] = { state: 'error', error: getErrorMessage(err) };
        }
      } else {
        for (const row of g.rows) {
          const key = `person:${row.addr}`;
          // ponytail: no vertical picker in the quick-add row (matches the
          // spec's "name + company picker" only) — relies on
          // initialVerticalId()'s auto-pick; a multi-vertical admin viewing
          // "All" verticals sees this row error until they switch the
          // header vertical first.
          const form = bulkPersonForm[row.addr];
          if (!form?.vertical_id || (!form.first_name.trim() && !form.last_name.trim())) {
            bulkStatus[key] = { state: 'error', error: 'Name and vertical are required' };
            continue;
          }
          bulkStatus[key] = { state: 'running' };
          try {
            await peopleApi.create({
              vertical_id: form.vertical_id,
              first_name: form.first_name.trim(),
              last_name: form.last_name.trim(),
              email: row.addr,
              company_id: form.company_id ?? undefined
            });
            await backfillApi.start(row.addr);
            bulkStatus[key] = { state: 'done' };
          } catch (err) {
            bulkStatus[key] = { state: 'error', error: getErrorMessage(err) };
          }
        }
      }
    }
    bulkSubmitting = false;
    selected = new Set();
    invalidateReview();
    queryClient.invalidateQueries({ queryKey: [...queryKey, 'backfill-status'] });
  }
</script>

<div>
  {#if ignoreError}<p class="tym-error-state mb-3">{ignoreError}</p>{/if}

  <div class="mb-3">
    <div class="mb-2.5 flex flex-wrap items-end justify-between gap-2">
      <div>
        <h2 class="tym-section-title">Review queue</h2>
        <div class="tym-section-sub">Senders that don&apos;t match a person or tracked domain yet</div>
      </div>
      {#if groups.length > 0}
        <label class="flex min-h-11 items-center gap-2 text-[12.5px] text-dark-gray">
          <input type="checkbox" class="h-4 w-4" checked={allSelected} onchange={toggleSelectAll} />
          Select all ({groups.length})
        </label>
      {/if}
    </div>
    <!-- sort as a chip row rather than two full-height buttons: same density
         as every other filtered list in the app, and it pans on a phone -->
    <FilterChipRow
      chips={[
        { id: 'newest', label: 'Newest' },
        { id: 'count', label: 'Most emails' }
      ]}
      activeId={sortMode}
      onSelect={(id) => (sortMode = id as typeof sortMode)}
      ariaLabel="Sort review queue"
    />
  </div>

  {#if reviewQuery.isLoading}
    <p class="tym-loading-state">Loading&hellip;</p>
  {:else if reviewQuery.isError}
    <p class="tym-error-state">
      {isAccessRevokedError(reviewQuery.error)
        ? 'Email access is disabled for your account — ask an admin.'
        : getErrorMessage(reviewQuery.error)}
    </p>
  {:else if groups.length === 0}
    <p class="tym-empty-state">No unreviewed senders — new ones appear as your mail syncs.</p>
  {:else}
    <div class="space-y-2.5">
      {#each groups as row (row.addr)}
        {@const bfState = rowBackfillState(row)}
        <div class="tym-card px-4 py-3.5">
          <div class="flex flex-wrap items-start justify-between gap-3">
            <div class="flex min-w-0 items-start gap-2.5">
              <input
                type="checkbox"
                class="mt-1 shrink-0"
                checked={selected.has(row.addr)}
                onchange={() => toggleRow(row.addr)}
                aria-label={`Select ${row.addr}`}
              />
              <div class="min-w-0">
                <div class="text-[13.5px] font-semibold text-navy">{row.name || row.addr}</div>
                {#if row.name}<div class="text-[11.5px] text-dark-gray">{row.addr}</div>{/if}
                <div class="mt-1 text-[12px] text-dark-gray">
                  {row.total} email{row.total === 1 ? '' : 's'} &middot; {row.in_count} in / {row.out_count} out &middot;
                  {row.domain}
                </div>
                {#if row.latest_subject || row.latest_sent_at}
                  <div class="mt-1 truncate text-[12px] text-dark-gray">
                    {#if row.latest_subject}<span class="italic">{row.latest_subject}</span>{/if}
                    {#if row.latest_sent_at}&nbsp;&middot; {timeAgo(row.latest_sent_at)}{/if}
                  </div>
                {/if}
                <div class="mt-1 text-[11px] text-dark-gray">
                  First seen {new Date(row.first_seen).toLocaleDateString()}
                </div>
              </div>
            </div>
            <div class="flex shrink-0 items-center gap-1.5">
              {#if bfState?.kind === 'running'}
                <span class="flex items-center gap-1.5 whitespace-nowrap text-[12px] font-medium text-dark-gray">
                  <LoaderCircle class="h-4 w-4 animate-spin" /> Importing history&hellip;
                </span>
              {:else if bfState?.kind === 'queued'}
                <span class="flex items-center gap-1.5 whitespace-nowrap text-[12px] font-medium text-dark-gray">
                  <LoaderCircle class="h-4 w-4" /> Queued ({bfState.position})
                </span>
              {:else}
                <button
                  type="button"
                  class="btn-secondary !h-10 !w-10 !p-0"
                  title="Add person"
                  aria-label="Add person"
                  onclick={() => openAddPerson(row)}
                >
                  <UserPlus class="h-4 w-4" />
                </button>
                <button
                  type="button"
                  class="btn-secondary !h-10 !w-10 !p-0"
                  title="Link to existing person"
                  aria-label="Link to existing person"
                  onclick={() => openLinkPerson(row)}
                >
                  <Link2 class="h-4 w-4" />
                </button>
                {#if hotelsVerticals.length > 0}
                  <button
                    type="button"
                    class="btn-secondary !h-10 !w-10 !p-0"
                    title="Attach to site"
                    aria-label="Attach to site"
                    onclick={() => openAttachSite(row)}
                  >
                    <MapPin class="h-4 w-4" />
                  </button>
                {/if}
                <button
                  type="button"
                  class="btn-secondary !h-10 !w-10 !p-0"
                  title="Mark as internal (one of our own people)"
                  aria-label="Mark as internal"
                  onclick={() => markInternalRow(row)}
                >
                  <UserCheck class="h-4 w-4" />
                </button>
                {#if session.isAdmin}
                  <button
                    type="button"
                    class="btn-secondary !h-10 !w-10 !p-0"
                    title="Add company & track domain"
                    aria-label="Add company & track domain"
                    onclick={() => openAddCompany(row)}
                  >
                    <Building2 class="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    class="btn-secondary !h-10 !w-10 !p-0"
                    title="Track domain only"
                    aria-label="Track domain only"
                    onclick={() => openTrackDomain(row)}
                  >
                    <Globe class="h-4 w-4" />
                  </button>
                {/if}
                <button
                  type="button"
                  class="btn-danger !h-10 !w-10 !p-0"
                  title="Ignore"
                  aria-label="Ignore"
                  onclick={() => ignoreRow(row)}
                >
                  <EyeOff class="h-4 w-4" />
                </button>
              {/if}
            </div>
          </div>
        </div>
      {/each}
    </div>
    <!-- keeps the last row clear of the pinned selection bar -->
    {#if selected.size > 0}<div class="h-16" aria-hidden="true"></div>{/if}
  {/if}
</div>

<!-- Pinned rather than inline above the list: the queue is long, and the
     old inline bar scrolled out of reach as soon as you started selecting. -->
<SelectionBar count={selected.size} noun="sender" onClear={() => (selected = new Set())}>
  <button
    type="button"
    class="btn-primary !py-2 !text-[12.5px]"
    onclick={openBulkImport}
    disabled={bulkInternalMutation.isPending || bulkIgnoreMutation.isPending}
  >
    <Users class="h-4 w-4" />
    Import&hellip;
  </button>
  <button
    type="button"
    class="btn-secondary !py-2 !text-[12.5px]"
    onclick={bulkMarkInternal}
    disabled={bulkInternalMutation.isPending || bulkIgnoreMutation.isPending}
  >
    <UserCheck class="h-4 w-4" />
    Internal
  </button>
  <button
    type="button"
    class="btn-danger !py-2 !text-[12.5px]"
    onclick={bulkIgnore}
    disabled={bulkInternalMutation.isPending || bulkIgnoreMutation.isPending}
  >
    <EyeOff class="h-4 w-4" />
    Ignore
  </button>
</SelectionBar>

{#if addPersonRow}
  <Modal title="Add person" onClose={() => (addPersonRow = null)}>
    <form class="space-y-4" onsubmit={submitAddPerson}>
      <div>
        <label class="label" for="add-person-vertical">Vertical</label>
        {#if session.me && session.me.verticals.length > 1}
          <select id="add-person-vertical" class="input" bind:value={addPersonForm.vertical_id}>
            {#if addPersonForm.vertical_id === undefined}
              <option value={undefined} disabled selected>Select vertical&hellip;</option>
            {/if}
            {#each session.me.verticals as v (v.id)}<option value={v.id}>{v.name}</option>{/each}
          </select>
        {:else}
          <select id="add-person-vertical" class="input" disabled>
            <option>{session.me?.verticals[0]?.name ?? '—'}</option>
          </select>
        {/if}
      </div>
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label class="label" for="add-person-first">First name</label>
          <input id="add-person-first" class="input" bind:value={addPersonForm.first_name} />
        </div>
        <div>
          <label class="label" for="add-person-last">Last name</label>
          <input id="add-person-last" class="input" bind:value={addPersonForm.last_name} />
        </div>
      </div>
      <div>
        <label class="label" for="add-person-company">Company (optional)</label>
        <CompanyCombobox
          id="add-person-company"
          bind:value={addPersonForm.company_id}
          verticalId={addPersonForm.vertical_id}
          placeholder="Search or add a company…"
        />
      </div>
      <div>
        <span class="label">Email</span>
        <p class="text-[13px] font-medium text-navy">{addPersonRow.addr}</p>
      </div>
      {#if showAddPersonTrackDomain}
        <label class="flex items-center gap-2 text-[12.5px] text-dark-gray">
          <input type="checkbox" bind:checked={addPersonAlsoTrackDomain} />
          Also allow entire @{addPersonRow.domain}
        </label>
      {/if}
      {#if addPersonError}<p class="text-sm text-red-600">{addPersonError}</p>{/if}
      <div class="flex justify-end gap-2 pt-2">
        <button type="button" class="btn-secondary" onclick={() => (addPersonRow = null)}>Cancel</button>
        <button
          type="submit"
          class="btn-primary"
          disabled={addPersonMutation.isPending || addPersonForm.vertical_id === undefined}
        >
          {addPersonMutation.isPending ? 'Adding...' : 'Add & import history'}
        </button>
      </div>
    </form>
  </Modal>
{/if}

{#if linkPersonRow}
  <Modal title="Link to existing person" onClose={() => (linkPersonRow = null)}>
    <div class="space-y-4">
      <div>
        <span class="label">Email to link</span>
        <p class="text-[13px] font-medium text-navy">{linkPersonRow.addr}</p>
      </div>
      <div>
        <label class="label" for="link-person">Person</label>
        <PersonCombobox
          id="link-person"
          bind:value={linkPersonId}
          verticalId={session.selectedVertical === 'all' ? undefined : session.selectedVertical}
          showVertical
        />
      </div>
      {#if linkPersonId !== undefined && linkCandidateQuery.data}
        <p class="text-[12.5px] text-dark-gray">
          Emails from <span class="font-semibold text-navy">{linkPersonRow.addr}</span> will be associated into
          <span class="font-semibold text-navy">{verticalName(linkCandidateQuery.data.vertical_id)}</span>.
        </p>
      {/if}
      {#if linkPersonId !== undefined && linkCandidateQuery.data?.email && linkCandidateQuery.data.email !== linkPersonRow.addr}
        <p class="text-[12.5px] text-amber-700">
          This person already has the email &quot;{linkCandidateQuery.data.email}&quot; &mdash; linking will overwrite
          it.
        </p>
      {/if}
      {#if showLinkTrackDomain}
        <label class="flex items-center gap-2 text-[12.5px] text-dark-gray">
          <input type="checkbox" bind:checked={linkAlsoTrackDomain} />
          Also allow entire @{linkPersonRow.domain}
        </label>
      {/if}
      {#if linkPersonError}<p class="text-sm text-red-600">{linkPersonError}</p>{/if}
      <div class="flex justify-end gap-2 pt-2">
        <button type="button" class="btn-secondary" onclick={() => (linkPersonRow = null)}>Cancel</button>
        <button type="button" class="btn-primary" onclick={confirmLinkPerson} disabled={linkPersonMutation.isPending}>
          {linkPersonMutation.isPending ? 'Linking...' : 'Link & import history'}
        </button>
      </div>
    </div>
  </Modal>
{/if}

{#if attachSiteRow}
  <Modal title="Attach to site" onClose={() => (attachSiteRow = null)}>
    <form class="space-y-4" onsubmit={submitAttachSite}>
      <p class="text-[12.5px] text-dark-gray">
        Creates a person for <span class="font-semibold text-navy">{attachSiteRow.addr}</span> and links them to a
        property in one step — pick an existing property or add a new one.
      </p>
      <div>
        <label class="label" for="attach-site-vertical">Vertical</label>
        {#if hotelsVerticals.length > 1}
          <select id="attach-site-vertical" class="input" bind:value={attachSiteForm.vertical_id}>
            {#if attachSiteForm.vertical_id === undefined}
              <option value={undefined} disabled selected>Select vertical&hellip;</option>
            {/if}
            {#each hotelsVerticals as v (v.id)}<option value={v.id}>{v.name}</option>{/each}
          </select>
        {:else}
          <select id="attach-site-vertical" class="input" disabled>
            <option>{hotelsVerticals[0]?.name ?? '—'}</option>
          </select>
        {/if}
      </div>
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label class="label" for="attach-site-first">First name</label>
          <input id="attach-site-first" class="input" bind:value={attachSiteForm.first_name} />
        </div>
        <div>
          <label class="label" for="attach-site-last">Last name</label>
          <input id="attach-site-last" class="input" bind:value={attachSiteForm.last_name} />
        </div>
      </div>
      <div>
        <label class="label" for="attach-site-hotel">Site (hotel/property)</label>
        <CompanyCombobox
          id="attach-site-hotel"
          bind:value={attachSiteForm.hotel_company_id}
          verticalId={attachSiteForm.vertical_id}
          kind="hotel"
          onSelect={onAttachHotelSelect}
          placeholder="Search or add a property…"
        />
      </div>
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label class="label" for="attach-site-brand">Brand</label>
          <CompanyCombobox
            id="attach-site-brand"
            bind:value={attachSiteForm.brand_company_id}
            verticalId={attachSiteForm.vertical_id}
            kind="brand"
          />
        </div>
        <div>
          <label class="label" for="attach-site-owner">Group</label>
          <CompanyCombobox
            id="attach-site-owner"
            bind:value={attachSiteForm.owner_company_id}
            verticalId={attachSiteForm.vertical_id}
            kind="owner"
          />
        </div>
      </div>
      {#if attachSiteError}<p class="text-sm text-red-600">{attachSiteError}</p>{/if}
      <div class="flex justify-end gap-2 pt-2">
        <button type="button" class="btn-secondary" onclick={() => (attachSiteRow = null)}>Cancel</button>
        <button
          type="submit"
          class="btn-primary"
          disabled={attachSiteMutation.isPending || attachSiteForm.vertical_id === undefined}
        >
          {attachSiteMutation.isPending ? 'Attaching...' : 'Attach & import history'}
        </button>
      </div>
    </form>
  </Modal>
{/if}

{#if addCompanyRow}
  <Modal title="Add company & track domain" onClose={() => (addCompanyRow = null)}>
    <form class="space-y-4" onsubmit={submitAddCompany}>
      <div>
        <label class="label" for="add-company-name">Company name</label>
        <input id="add-company-name" class="input" bind:value={addCompanyForm.name} required />
      </div>
      <div>
        <label class="label" for="add-company-city">Location</label>
        <input id="add-company-city" class="input" bind:value={addCompanyForm.city} placeholder="City / area" />
      </div>
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label class="label" for="add-company-kind">Type</label>
          <select id="add-company-kind" class="input" bind:value={addCompanyForm.kind}>
            <option value="company">Company</option>
            <option value="hotel">Hotel</option>
            <option value="brand">Brand</option>
            <option value="owner">Owner</option>
          </select>
        </div>
        <div>
          <label class="label" for="add-company-vertical">Vertical</label>
          {#if session.me && session.me.verticals.length > 1}
            <select id="add-company-vertical" class="input" bind:value={addCompanyForm.vertical_id}>
              {#if addCompanyForm.vertical_id === undefined}
                <option value={undefined} disabled selected>Select vertical&hellip;</option>
              {/if}
              {#each session.me.verticals as v (v.id)}<option value={v.id}>{v.name}</option>{/each}
            </select>
          {:else}
            <select id="add-company-vertical" class="input" disabled>
              <option>{session.me?.verticals[0]?.name ?? '—'}</option>
            </select>
          {/if}
        </div>
      </div>
      <p class="text-[12px] text-dark-gray">
        Tracks &quot;{addCompanyRow.domain}&quot; against this company so future mail from that domain files
        automatically.
      </p>
      {#if addCompanyError}<p class="text-sm text-red-600">{addCompanyError}</p>{/if}
      <div class="flex justify-end gap-2 pt-2">
        <button type="button" class="btn-secondary" onclick={() => (addCompanyRow = null)}>Cancel</button>
        <button
          type="submit"
          class="btn-primary"
          disabled={addCompanyMutation.isPending || addCompanyForm.vertical_id === undefined}
        >
          {addCompanyMutation.isPending ? 'Adding...' : 'Add & import history'}
        </button>
      </div>
    </form>
  </Modal>
{/if}

{#if trackDomainRow}
  <Modal title="Track domain only" onClose={() => (trackDomainRow = null)}>
    <form class="space-y-4" onsubmit={submitTrackDomain}>
      <p class="text-[12.5px] text-dark-gray">Pick the existing company that owns &quot;{trackDomainRow.domain}&quot;.</p>
      <div>
        <label class="label" for="track-domain-company">Company</label>
        <CompanyCombobox
          id="track-domain-company"
          bind:value={trackDomainCompanyId}
          bind:selectedVerticalId={trackDomainVerticalId}
          bind:selectedName={trackDomainCompanyName}
          placeholder="Search or add a company…"
        />
      </div>
      {#if trackDomainCompanyId !== undefined}
        <p class="text-[12.5px] text-dark-gray">
          Domain will be tracked in
          <span class="font-semibold text-navy">{verticalName(trackDomainVerticalId)}</span> &mdash; inherited from
          <span class="font-semibold text-navy">{trackDomainCompanyName ?? 'the selected company'}</span>.
        </p>
      {/if}
      {#if trackDomainError}<p class="text-sm text-red-600">{trackDomainError}</p>{/if}
      <div class="flex justify-end gap-2 pt-2">
        <button type="button" class="btn-secondary" onclick={() => (trackDomainRow = null)}>Cancel</button>
        <button
          type="submit"
          class="btn-primary"
          disabled={trackDomainMutation.isPending || trackDomainCompanyId === undefined}
        >
          {trackDomainMutation.isPending ? 'Tracking...' : 'Track & import history'}
        </button>
      </div>
    </form>
  </Modal>
{/if}

{#if showBulkImport}
  <Modal
    title={`Import ${bulkRows.length} selected sender${bulkRows.length === 1 ? '' : 's'}`}
    onClose={() => !bulkSubmitting && (showBulkImport = false)}
    wide
  >
    <div class="space-y-4">
      {#each bulkDomainGroups as g (g.domain)}
        {#if g.corporate}
          {@const key = `domain:${g.domain}`}
          <div class="tym-card px-4 py-3">
            <div class="mb-2 text-[13px] font-semibold text-navy">
              @{g.domain}
              <span class="font-normal text-dark-gray"
                >({g.rows.length} sender{g.rows.length === 1 ? '' : 's'} &mdash; tracked as one domain)</span
              >
            </div>
            <CompanyCombobox
              bind:value={bulkDomainForm[g.domain].company_id}
              bind:selectedVerticalId={bulkDomainForm[g.domain].vertical_id}
              bind:selectedName={bulkDomainForm[g.domain].company_name}
              placeholder="Search or add a company…"
            />
            {#if bulkStatus[key]}
              <p class="mt-1.5 text-[12px] {bulkStatus[key].state === 'error' ? 'text-red-600' : 'text-dark-gray'}">
                {#if bulkStatus[key].state === 'running'}Tracking &amp; importing&hellip;
                {:else if bulkStatus[key].state === 'done'}Tracked — importing in the background.
                {:else}{bulkStatus[key].error}{/if}
              </p>
            {/if}
          </div>
        {:else}
          {#each g.rows as row (row.addr)}
            {@const key = `person:${row.addr}`}
            <div class="tym-card px-4 py-3">
              <div class="mb-2 text-[12.5px] text-navy">
                <span class="font-semibold">{row.name || row.addr}</span>
                {#if row.name}<span class="text-dark-gray"> &mdash; {row.addr}</span>{/if}
              </div>
              <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <input class="input" placeholder="First name" bind:value={bulkPersonForm[row.addr].first_name} />
                <input class="input" placeholder="Last name" bind:value={bulkPersonForm[row.addr].last_name} />
              </div>
              <div class="mt-2">
                <CompanyCombobox
                  bind:value={bulkPersonForm[row.addr].company_id}
                  verticalId={bulkPersonForm[row.addr].vertical_id}
                  placeholder="Company (optional)"
                />
              </div>
              {#if bulkStatus[key]}
                <p class="mt-1.5 text-[12px] {bulkStatus[key].state === 'error' ? 'text-red-600' : 'text-dark-gray'}">
                  {#if bulkStatus[key].state === 'running'}Adding &amp; importing&hellip;
                  {:else if bulkStatus[key].state === 'done'}Added — importing in the background.
                  {:else}{bulkStatus[key].error}{/if}
                </p>
              {/if}
            </div>
          {/each}
        {/if}
      {/each}
      <div class="flex justify-end gap-2 pt-2">
        <button type="button" class="btn-secondary" onclick={() => (showBulkImport = false)} disabled={bulkSubmitting}>
          {bulkSubmitting ? 'Close' : 'Cancel'}
        </button>
        <button type="button" class="btn-primary" onclick={submitBulkImport} disabled={bulkSubmitting || bulkRows.length === 0}>
          {bulkSubmitting ? 'Importing…' : `Import ${bulkRows.length} sender${bulkRows.length === 1 ? '' : 's'}`}
        </button>
      </div>
    </div>
  </Modal>
{/if}
